/**
 * The page map: what the machine read out of a bundle, and what the officer
 * decides it is.
 *
 * OCR on a photocopy is rough. The way this screen makes rough safe is that it
 * files nothing on its own — every row is a *proposal* until an officer gives it
 * a disposition, and the counter at the top will not let Confirm go until every
 * page in the bundle has one.
 *
 * Four things here are the product rather than the layout:
 *
 * **The accounting counter.** "94 of 214 pages accounted for · 3 groups need
 * your attention." Confirm is disabled below that number and **stays on screen**
 * carrying the reason — a control that vanishes teaches nothing, and the reason
 * is the instruction. The server refuses the same request identically; the
 * greying is a courtesy, not the gate.
 *
 * **Stacked authorship.** *Detected: …* stays visible and muted above *Set by
 * … · 14:22*. The machine's finding is never replaced by the human's — the row
 * carries both for as long as the case file exists.
 *
 * **Unreadable is a state, not an absence.** Pages OCR could not read are
 * counted in the header and marked by the officer, never dropped from the total.
 *
 * **Nothing is hidden.** A detection that matched two documents shows both and
 * chooses neither. A failed job shows its error. A duplicate upload is listed
 * rather than silently deduplicated.
 *
 * No document title is written in this file. The type list comes off the case's
 * own rail, which comes off the pinned process version (stop list 6).
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import type {
  AccountingView,
  CaseDetail,
  Disposition,
  IngestionJobDetail,
  SegmentView,
} from '../api/client'
import { ErrorEnvelope } from '../components/bits'

/** The six the detector can produce, plus the one only an officer creates. */
const STATE_WORDS: Record<string, string> = {
  confident: 'Confident',
  'low-confidence': 'Low confidence',
  unclassified: 'Unclassified',
  'boundary-uncertain': 'Boundary uncertain',
  duplicate: 'Duplicate',
  'different-case': 'Different case',
  'set-by-officer': 'Set by officer',
}

const STATE_TONE: Record<string, string> = {
  confident: 'good',
  'low-confidence': 'warn',
  unclassified: 'neutral',
  'boundary-uncertain': 'warn',
  duplicate: 'neutral',
  'different-case': 'warn',
  'set-by-officer': 'accent',
}

const DISPOSITION_WORDS: Record<Disposition, string> = {
  DOCUMENT: 'Document',
  ANNEXURE: 'Annexure of',
  NOT_RELEVANT: 'Not relevant',
  UNREADABLE: 'Unreadable',
}

/** States in which the worker is still doing something, so the screen polls. */
const RUNNING = new Set(['QUEUED', 'CHUNKING', 'OCR', 'ANALYSING'])

/** States the server will actually re-queue — `reset_for_retry` refuses the rest. */
const RETRYABLE = new Set(['FAILED', 'OCR'])

export function PageMap() {
  const { caseId, jobId } = useParams()
  const [params] = useSearchParams()
  /**
   * The page a chat citation came from.
   *
   * OCR text is cited by page, and the page map groups pages into segments — so
   * following the citation lands on the group the page falls in, which is what
   * an officer needs in order to see the disposition beside the scan.
   */
  const citedPage = Number(params.get('p') ?? '')
  const client = useQueryClient()

  const bundle = useQuery({
    queryKey: ['bundle', caseId, jobId],
    queryFn: () => api.getBundle(caseId as string, jobId as string),
    enabled: Boolean(caseId && jobId),
    // Only while the worker is working. A bundle sitting at REVIEW_READY is
    // waiting for the officer, and a screen that refetches under their hands
    // loses the row they were part-way through.
    refetchInterval: (query) => (RUNNING.has(query.state.data?.state ?? '') ? 2000 : false),
  })

  const detail = useQuery({
    queryKey: ['case', caseId],
    queryFn: () => api.getCase(caseId as string),
    enabled: Boolean(caseId),
  })

  const [receipt, setReceipt] = useState<string | null>(null)
  const [failure, setFailure] = useState<unknown>(null)

  const confirm = useMutation({
    mutationFn: () =>
      api.confirmBundle(caseId as string, jobId as string, { preflight: false }),
    onSuccess: (result) => {
      setFailure(null)
      setReceipt(
        `${result.documents_filed} document${result.documents_filed === 1 ? '' : 's'} filed · ` +
          `${result.pages_excluded} page${result.pages_excluded === 1 ? '' : 's'} excluded · ` +
          `${result.pages_unreadable} unreadable`,
      )
      void client.invalidateQueries({ queryKey: ['bundle', caseId, jobId] })
      void client.invalidateQueries({ queryKey: ['case', caseId] })
    },
    onError: setFailure,
  })

  const byHand = useMutation({
    mutationFn: () => api.reviewBundleByHand(caseId as string, jobId as string),
    onSuccess: (next) => {
      setFailure(null)
      client.setQueryData(['bundle', caseId, jobId], next)
    },
    onError: setFailure,
  })

  const retry = useMutation({
    mutationFn: () => api.retryBundle(caseId as string, jobId as string),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['bundle', caseId, jobId] }),
    onError: setFailure,
  })

  if (bundle.isLoading) return <div className="empty">Reading the bundle…</div>
  if (bundle.error) {
    return (
      <div className="page">
        <ErrorEnvelope error={bundle.error} />
        <Link to={`/cases/${caseId}`}>Back to the case</Link>
      </div>
    )
  }
  if (!bundle.data) return <div className="empty">No such bundle.</div>

  const job = bundle.data
  const counts = job.accounting
  // The generated types make a defaulted array optional; the server always
  // sends both. Reading through a default here rather than asserting keeps the
  // screen honest if a future version drops one.
  const segments = job.segments ?? []
  const duplicates = job.duplicate_of ?? []
  const missing = counts.missing_pages ?? []

  return (
    <div className="page">
      <header>
        <div>
          <h1>Page map</h1>
          <p className="mono small muted">{job.filename}</p>
        </div>
        <Link to={`/cases/${caseId}`}>Back to the case</Link>
      </header>

      <Progress
        job={job}
        duplicates={duplicates.length}
        onRetry={() => retry.mutate()}
        retrying={retry.isPending}
      />

      <Accounting counts={counts} />

      {failure ? <ErrorEnvelope error={failure} /> : null}
      {receipt ? (
        <div className="card" data-testid="bundle-receipt">
          <div className="body">Filed. {receipt}</div>
        </div>
      ) : null}

      <div className="card">
        <header>
          <span>Groups of pages</span>
          <span className="small muted">
            {segments.length} group{segments.length === 1 ? '' : 's'}
          </span>
        </header>
        <div className="body">
          {segments.length === 0 ? (
            <p className="muted">
              No groups yet.{' '}
              {RUNNING.has(job.state)
                ? 'The bundle is still being read.'
                : 'Nothing was read out of this bundle — see the job’s state above.'}
            </p>
          ) : (
            segments.map((segment) => (
              <SegmentRow
                key={segment.id}
                caseId={caseId as string}
                jobId={jobId as string}
                segment={segment}
                cited={
                  Number.isFinite(citedPage) &&
                  citedPage >= segment.first_page &&
                  citedPage <= segment.last_page
                }
                confirmed={job.state === 'CONFIRMED'}
                types={documentTypes(detail.data)}
                onChanged={(next) => client.setQueryData(['bundle', caseId, jobId], next)}
                onError={setFailure}
              />
            ))
          )}
        </div>
      </div>

      {missing.length > 0 && job.state !== 'CONFIRMED' ? (
        <div className="card">
          <div className="body">
            <p className="small">
              {missing.length} page{missing.length === 1 ? '' : 's'} of this bundle are in no
              group, because nothing read them. They can still be put on the page map and
              assigned from the scan — nothing is filed until you decide what each one is.
            </p>
            <button
              className="btn"
              data-testid="review-by-hand"
              disabled={byHand.isPending}
              onClick={() => byHand.mutate()}
            >
              {byHand.isPending ? 'Adding…' : 'Put them on the page map'}
            </button>
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="body row">
          {/* Disabled, never hidden, and the reason travels with it. */}
          <button
            className="btn primary"
            data-testid="confirm-bundle"
            disabled={!counts.can_confirm || job.state === 'CONFIRMED' || confirm.isPending}
            onClick={() => confirm.mutate()}
          >
            {job.state === 'CONFIRMED' ? 'Filed' : 'Confirm and file'}
          </button>
          {counts.blocked_reason ? (
            <span className="small muted" data-testid="confirm-blocked-reason">
              {counts.blocked_reason}
            </span>
          ) : null}
        </div>
      </div>

      <Pages caseId={caseId as string} jobId={jobId as string} segments={segments} />
    </div>
  )
}

/* ------------------------------------------------------------- the counter */

function Accounting({ counts }: { counts: AccountingView }) {
  return (
    <div className="card" data-testid="accounting">
      <div className="body">
        <p style={{ fontSize: '1.1rem', margin: 0 }}>
          <strong data-testid="accounting-counter">
            {counts.accounted_pages} of {counts.total_pages} pages accounted for
          </strong>
          {counts.segments_needing_decision > 0 ? (
            <>
              {' · '}
              <span data-testid="accounting-attention">
                {counts.segments_needing_decision} group
                {counts.segments_needing_decision === 1 ? '' : 's'} need
                {counts.segments_needing_decision === 1 ? 's' : ''} your attention
              </span>
            </>
          ) : null}
        </p>
        <p className="small muted" style={{ marginBottom: 0 }}>
          {counts.excluded_pages} excluded as not relevant · {counts.unreadable_pages}{' '}
          unreadable · {counts.filing_segments} group{counts.filing_segments === 1 ? '' : 's'}{' '}
          will be filed
        </p>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------- progress */

function Progress({
  job,
  duplicates,
  onRetry,
  retrying,
}: {
  job: IngestionJobDetail
  duplicates: number
  onRetry: () => void
  retrying: boolean
}) {
  const stateWords: Record<string, string> = {
    QUEUED: 'Queued',
    CHUNKING: 'Splitting the bundle',
    OCR: 'Reading pages',
    ANALYSING: 'Grouping pages',
    REVIEW_READY: 'Ready for your review',
    CONFIRMED: 'Filed',
    FAILED: 'Could not be read',
  }
  return (
    <div className="card">
      <header>
        <span data-testid="bundle-state">{stateWords[job.state] ?? job.state}</span>
        <span className="small muted mono">
          {job.chunks_done}/{job.chunks_total} requests · {job.pages_done}/
          {job.pages_total ?? '—'} pages
        </span>
      </header>
      <div className="body">
        {RUNNING.has(job.state) && job.minutes_at_the_cap > 0 ? (
          <p className="small muted">
            Doc AI accepts ten pages a request and ten requests a minute. At that cap this
            bundle needs at least {job.minutes_at_the_cap.toFixed(1)} more minutes — the limit
            is Sarvam’s, not a measure of how fast the pages are being read.
          </p>
        ) : null}
        {job.unreadable_pages > 0 ? (
          <p className="small">
            {job.unreadable_pages} page{job.unreadable_pages === 1 ? '' : 's'} could not be read
            by OCR. They are counted in the total and must still be accounted for.
          </p>
        ) : null}
        {duplicates > 0 ? (
          <p className="small">
            The identical file has already been uploaded to this case
            {duplicates > 1 ? ` ${duplicates} times` : ''}. Both are kept — only you can say
            whether this one was deliberate.
          </p>
        ) : null}
        {job.error ? (
          <div className="envelope">
            <p>{job.error}</p>
            {/* Greyed, not hidden, and carrying why. The error stays on a
                bundle that was put on the page map by hand — deliberately, so
                the file records that OCR never read it — and the button was
                enabled on exactly that path, returning a 409 into the envelope
                below it. */}
            <button
              className="btn"
              data-testid="retry-bundle"
              onClick={onRetry}
              disabled={retrying || !RETRYABLE.has(job.state)}
            >
              {retrying ? 'Re-queueing…' : 'Try reading it again'}
            </button>
            {!RETRYABLE.has(job.state) ? (
              <span className="small muted" data-testid="retry-blocked-reason">
                {' '}
                This bundle is {job.state === 'CONFIRMED' ? 'filed' : 'open for your review'},
                so there is nothing to re-read. The error above is kept because it is why the
                pages were never read.
              </span>
            ) : null}
            <p className="small muted" style={{ marginBottom: 0 }}>
              Pages Doc AI has already accepted are not sent again — they are collected where
              they were left.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------- one row */

function SegmentRow({
  caseId,
  jobId,
  segment,
  cited,
  confirmed,
  types,
  onChanged,
  onError,
}: {
  caseId: string
  jobId: string
  segment: SegmentView
  cited: boolean
  confirmed: boolean
  types: { value: string; label: string }[]
  onChanged: (next: IngestionJobDetail) => void
  onError: (error: unknown) => void
}) {
  const [disposition, setDisposition] = useState<Disposition | ''>(
    (segment.disposition as Disposition | null) ?? '',
  )
  const [documentType, setDocumentType] = useState(
    segment.document_type ?? segment.detected_document_type ?? '',
  )
  const [documentDate, setDocumentDate] = useState(segment.document_date ?? '')
  const [splitAt, setSplitAt] = useState('')

  const assign = useMutation({
    mutationFn: () =>
      api.assignSegment(caseId, jobId, segment.id, {
        preflight: false,
        disposition: disposition === '' ? null : disposition,
        document_type: documentType || null,
        annexure_of_type: disposition === 'ANNEXURE' ? documentType || null : null,
        document_date: documentDate || null,
      }),
    onSuccess: onChanged,
    onError,
  })

  const split = useMutation({
    mutationFn: () =>
      api.splitSegment(caseId, jobId, segment.id, {
        preflight: false,
        at_page: Number(splitAt),
      }),
    onSuccess: (next) => {
      setSplitAt('')
      onChanged(next)
    },
    onError,
  })

  const tone = STATE_TONE[segment.display_state] ?? 'neutral'
  const needsType = disposition === 'DOCUMENT' || disposition === 'ANNEXURE'

  const row = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (cited) row.current?.scrollIntoView({ block: 'center' })
  }, [cited])

  return (
    <div
      className={`doc${cited ? ' cited' : ''}`}
      data-testid="segment-row"
      data-segment-state={segment.display_state}
      ref={row}
    >
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span className="reg mono">
          pp. {segment.first_page}
          {segment.last_page === segment.first_page ? '' : `–${segment.last_page}`}
        </span>
        <span className={`chip ${tone}`}>
          {STATE_WORDS[segment.display_state] ?? segment.display_state}
        </span>
      </div>

      {/* Stacked authorship. The detection stays on the row after the officer
          has decided; it is muted, not removed. */}
      <p className="reason small muted" data-testid="segment-detected">
        Detected: {segment.detected_reason}
        {(segment.detected_alternatives ?? []).length > 0
          ? ` Either of: ${(segment.detected_alternatives ?? []).join(', ')}.`
          : ''}
      </p>
      {segment.adjusted_by ? (
        <p className="note small" data-testid="segment-adjusted">
          Set by {segment.adjusted_by}
          {segment.adjusted_at ? ` · ${new Date(segment.adjusted_at).toLocaleString()}` : ''}
        </p>
      ) : null}

      <div className="row" style={{ flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
        <label className="field">
          <span className="small muted">This group is</span>
          <select
            aria-label={`Disposition for pages ${segment.first_page} to ${segment.last_page}`}
            value={disposition}
            disabled={confirmed}
            onChange={(event) => setDisposition(event.target.value as Disposition | '')}
          >
            <option value="">Needs a decision</option>
            {(Object.keys(DISPOSITION_WORDS) as Disposition[]).map((value) => (
              <option key={value} value={value}>
                {DISPOSITION_WORDS[value]}
              </option>
            ))}
          </select>
        </label>

        {needsType ? (
          <label className="field">
            <span className="small muted">
              {disposition === 'ANNEXURE' ? 'Annexure of' : 'Which document'}
            </span>
            <select
              aria-label={`Document for pages ${segment.first_page} to ${segment.last_page}`}
              value={documentType}
              disabled={confirmed}
              onChange={(event) => setDocumentType(event.target.value)}
            >
              <option value="">Choose…</option>
              {types.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {disposition === 'DOCUMENT' ? (
          <label className="field">
            <span className="small muted">Date on the document</span>
            <input
              type="date"
              aria-label={`Date for pages ${segment.first_page} to ${segment.last_page}`}
              value={documentDate}
              disabled={confirmed}
              onChange={(event) => setDocumentDate(event.target.value)}
            />
          </label>
        ) : null}

        <button
          className="btn"
          data-testid="segment-save"
          disabled={confirmed || assign.isPending || (needsType && !documentType)}
          onClick={() => assign.mutate()}
        >
          {assign.isPending ? 'Saving…' : 'Record'}
        </button>
      </div>

      {/* Moving a boundary the detector put in the wrong place, or cutting a
          hand-added run into the documents it actually holds. The arithmetic is
          the server's, so a cut that would leave an empty group is refused
          there — an empty group accounts for no pages, can never be given a
          decision, and would shut the gate with nothing explaining why. */}
      {segment.last_page > segment.first_page && !confirmed ? (
        <div className="row" style={{ gap: 8, marginTop: 6 }}>
          <label className="field">
            <span className="small muted">Split, so this page starts a new group</span>
            <input
              type="number"
              min={segment.first_page + 1}
              max={segment.last_page}
              aria-label={`Split pages ${segment.first_page} to ${segment.last_page}`}
              value={splitAt}
              onChange={(event) => setSplitAt(event.target.value)}
            />
          </label>
          <button
            className="btn"
            data-testid="segment-split"
            disabled={split.isPending || !splitAt}
            onClick={() => split.mutate()}
          >
            {split.isPending ? 'Splitting…' : 'Split'}
          </button>
        </div>
      ) : null}
    </div>
  )
}

/* ------------------------------------------------------------ thumbnails */

/**
 * The bundle itself, one page per row of the map.
 *
 * Rendered by the browser's own PDF viewer rather than by a rasteriser here:
 * the file is streamed from the API with the session cookie, it never leaves
 * the system as a presigned URL, and there is no page image cache to keep in
 * step with a bundle that was re-uploaded.
 */
function Pages({
  caseId,
  jobId,
  segments,
}: {
  caseId: string
  jobId: string
  segments: SegmentView[]
}) {
  const frame = useRef<HTMLIFrameElement>(null)
  const [page, setPage] = useState(1)
  useEffect(() => {
    if (frame.current)
      frame.current.src = `/api/cases/${caseId}/ingestion-jobs/${jobId}/source#page=${page}`
  }, [caseId, jobId, page])

  const first = useMemo(() => segments.map((s) => s.first_page), [segments])

  return (
    <div className="card">
      <header>
        <span>The bundle</span>
        <span className="small muted">
          Jump to the first page of a group to check what the machine read.
        </span>
      </header>
      <div className="body">
        <div className="row" style={{ flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {first.map((pageNo) => (
            <button key={pageNo} className="btn small" onClick={() => setPage(pageNo)}>
              p. {pageNo}
            </button>
          ))}
        </div>
        <iframe
          ref={frame}
          title="The uploaded bundle"
          data-testid="bundle-pdf"
          style={{ width: '100%', height: 520, border: '1px solid var(--line)' }}
        />
      </div>
    </div>
  )
}

/**
 * The document types this case can carry, off its own rail.
 *
 * Not a list in this file. A case pinned to a corrected process version must
 * offer that version's documents, and a hardcoded twenty would be wrong on
 * exactly the cases where being wrong matters (stop list 6).
 */
function documentTypes(detail: CaseDetail | undefined): { value: string; label: string }[] {
  if (!detail) return []
  return detail.rail.map((row) => ({
    value: row.document_type,
    label: `${row.document_type} · ${row.short_title ?? row.title}`,
  }))
}
